/**
 * V1 Question Renderer
 * 
 * Handles all V1 question types including multi-answer questions
 * where a single question can have star rating + options + comment.
 */

import React from 'react';
import type { V1Question, V1QuestionResponse } from './types/v1-questionnaire.types';
import { 
  V1TextQuestion,
  V1TextareaQuestion,
  V1RadioQuestion,
  V1CheckboxQuestion,
  V1SelectQuestion,
  V1DateQuestion,
  V1StarRating
} from './questions';
import { V1QuestionHeader } from './V1QuestionHeader';
import { V1QuestionFooter } from './V1QuestionFooter';

interface V1QuestionRendererProps {
  question: V1Question;
  response: V1QuestionResponse;
  onChange: (response: V1QuestionResponse) => void;
  onBlur?: () => void;
  validationError?: string;
  showError?: boolean;
  primaryColor?: string;
}

export const V1QuestionRenderer: React.FC<V1QuestionRendererProps> = ({
  question,
  response,
  onChange,
  onBlur,
  validationError,
  showError = false,
  primaryColor = '#3B82F6',
}) => {
  // Handle star rating change
  const handleStarRatingChange = (starRating: number) => {
    onChange({
      ...response,
      questionId: question.id,
      starRating,
    });
  };

  // Handle main value change (for form questions and simple survey questions)
  const handleValueChange = (value: any) => {
    onChange({
      ...response,
      questionId: question.id,
      value,
    });
  };

  // Handle radio answer change (for survey questions)
  const handleAnswerIdChange = (answerId: string) => {
    onChange({
      ...response,
      questionId: question.id,
      answerId,
    });
  };

  // Handle checkbox answer change (for survey questions)
  const handleAnswerIdsChange = (answerIds: string[]) => {
    onChange({
      ...response,
      questionId: question.id,
      answerIds,
    });
  };

  // Handle comment change
  const handleCommentChange = (comment: string) => {
    onChange({
      ...response,
      questionId: question.id,
      comment,
    });
  };

  // Render the appropriate question input based on type
  const renderQuestionInput = () => {
    switch (question.type) {
      case 'text':
      case 'email':
      case 'number':
        return (
          <V1TextQuestion
            type={question.type}
            value={(response.value as string) || ''}
            onChange={handleValueChange}
            onBlur={onBlur}
            validations={question.validations}
            hasError={showError && !!validationError}
          />
        );

      case 'textarea':
        return (
          <V1TextareaQuestion
            value={(response.value as string) || ''}
            onChange={handleValueChange}
            onBlur={onBlur}
            validations={question.validations}
            hasError={showError && !!validationError}
          />
        );

      case 'radio':
        return (
          <V1RadioQuestion
            options={question.options || []}
            value={response.answerId || response.value as string || ''}
            onChange={(value) => {
              // For V1 surveys, we always use answerId for radio questions
              // Check if this looks like a survey context (has star rating or comment box features)
              const isSurveyContext = question.hasStarRating !== undefined || 
                                      question.hasCommentBox !== undefined ||
                                      response.starRating !== undefined ||
                                      response.answerId !== undefined ||
                                      response.answerIds !== undefined;
              
              if (isSurveyContext) {
                handleAnswerIdChange(value);
              } else {
                handleValueChange(value);
              }
            }}
            hasError={showError && !!validationError}
            primaryColor={primaryColor}
          />
        );

      case 'checkbox':
        return (
          <V1CheckboxQuestion
            options={question.options || []}
            value={response.answerIds || (Array.isArray(response.value) ? response.value as string[] : [])}
            onChange={(values) => {
              // For V1 surveys, we always use answerIds for checkbox questions
              // Check if this looks like a survey context (has star rating or comment box features)
              const isSurveyContext = question.hasStarRating !== undefined || 
                                      question.hasCommentBox !== undefined ||
                                      response.starRating !== undefined ||
                                      response.answerId !== undefined ||
                                      response.answerIds !== undefined;
              
              if (isSurveyContext) {
                handleAnswerIdsChange(values);
              } else {
                handleValueChange(values);
              }
            }}
            hasError={showError && !!validationError}
            primaryColor={primaryColor}
          />
        );

      case 'select':
        return (
          <V1SelectQuestion
            options={question.options || []}
            value={(response.value as string) || ''}
            onChange={handleValueChange}
            onBlur={onBlur}
            hasError={showError && !!validationError}
          />
        );

      case 'date':
        return (
          <V1DateQuestion
            value={(response.value as string) || ''}
            onChange={handleValueChange}
            onBlur={onBlur}
            hasError={showError && !!validationError}
          />
        );

      case 'file':
        return (
          <div className="text-gray-500">
            <p className="text-sm">File upload is not available in V1 forms</p>
          </div>
        );

      default:
        return (
          <p className="text-gray-500">Unsupported question type: {question.type}</p>
        );
    }
  };

  return (
    <div className="mb-6" id={`question-${question.id}`}>
      {/* V1 Multi-answer questions show components in this order: */}
      
      {/* 1. Star Rating (if present) */}
      {question.hasStarRating && (
        <div className="mb-4">
          {question.starRatingLabel && (
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.starRatingLabel}
            </label>
          )}
          <V1StarRating
            value={response.starRating || 0}
            onChange={handleStarRatingChange}
          />
        </div>
      )}

      {/* 2. Main Question (if not star rating only) */}
      {(!question.hasStarRating || question.options?.length > 0) && (
        <div className={question.hasStarRating ? 'mb-4' : ''}>
          <V1QuestionHeader
            text={question.text}
            required={question.required}
            helpText={question.helpText}
          />
          {renderQuestionInput()}
        </div>
      )}

      {/* 3. Comment Box (if present) */}
      {question.hasCommentBox && (
        <div className={question.hasStarRating || question.options?.length > 0 ? 'mt-4' : ''}>
          {question.commentPrompt && (
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {question.commentPrompt}
              {question.commentRequired && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          <V1TextareaQuestion
            value={response.comment || ''}
            onChange={handleCommentChange}
            onBlur={onBlur}
            placeholder={question.commentPrompt || 'Additional comments...'}
            rows={3}
            hasError={showError && question.commentRequired && !response.comment}
          />
        </div>
      )}

      {/* Error display */}
      <V1QuestionFooter
        error={showError ? validationError : undefined}
      />
    </div>
  );
};